import { useEffect, useState } from "react";

type System = { id: string; text: string; angleDeg: number };

const SYSTEMS: System[] = [
  { id: "crm", text: "CRM", angleDeg: 0 },
  { id: "billing", text: "Billing", angleDeg: 90 },
  { id: "ledger", text: "Ledger", angleDeg: 180 },
  { id: "contract", text: "Contract", angleDeg: 270 },
];

type Step = {
  word: string;
  active: string | null;
  caption: string;
  verified?: boolean;
};

// The live sequence: watch -> catch the drift -> reconcile -> prove. On canon.
const STEPS: Step[] = [
  { word: "WATCHING", active: null, caption: "Watching the systems your business runs on." },
  { word: "DRIFT CAUGHT", active: "billing", caption: "Billing reads $10k / mo. The contract is $120k / yr." },
  { word: "RECONCILING", active: "billing", caption: "Meshly reconciles the gap. A human approves." },
  { word: "VERIFIED", active: null, verified: true, caption: "Proven, and traceable to its source." },
];

// Systems orbit inside the Trust Harness ring (radiusPct < 50, the ring is the edge).
const ORBIT = 34;

const polar = (angleDeg: number, radiusPct: number) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    left: `${50 + radiusPct * Math.cos(rad)}%`,
    top: `${50 + radiusPct * Math.sin(rad)}%`,
  };
};

/**
 * The instrument — Meshly's hero visual, interactive.
 * The blue Trust Harness ring surrounds the business systems orbiting Meshly;
 * the signal watches, catches the one that drifts, proves it. White, ink, blue.
 */
export default function OrbitalMesh() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => setI((n) => (n + 1) % STEPS.length), 2400);
    return () => window.clearInterval(id);
  }, [paused]);

  const step = STEPS[i];

  return (
    <div
      className="flex flex-col items-center gap-6"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-64 w-64 sm:h-72 sm:w-72 lg:w-[24rem] lg:h-[24rem]">
        {/* faint concentric halo, same center as the Meshly core, behind everything.
            Desktop only — on mobile the big rings bleed off-screen, so we drop them
            and keep a contained instrument (harness + core + systems). */}
        <div className="absolute -inset-[3rem] hidden rounded-full border border-foreground/[0.15] lg:block" />
        <div className="absolute -inset-[7rem] hidden rounded-full border border-foreground/[0.13] lg:block" />
        <div className="absolute -inset-[12rem] hidden rounded-full border border-foreground/[0.11] xl:block" />

        {/* faint orbit track the systems sit on (scales with the container) */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-foreground/[0.26]"
          style={{ width: `${ORBIT * 2}%`, height: `${ORBIT * 2}%` }}
        />

        {/* the Trust Harness — the blue ring surrounding the systems */}
        <div className="absolute inset-0 rounded-full border-[2.5px] border-primary/70" />
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/30 bg-background px-2.5 py-0.5">
          <span className="font-mono-tech text-[10px] font-medium uppercase tracking-[0.16em] text-primary">
            Trust Harness
          </span>
        </div>

        {/* hairline connectors: Meshly to each system */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
          <line x1="50%" y1="50%" x2="50%" y2="16%" stroke="hsl(0 0% 0% / 0.07)" strokeWidth="1" />
          <line x1="50%" y1="50%" x2="84%" y2="50%" stroke="hsl(0 0% 0% / 0.07)" strokeWidth="1" />
          <line x1="50%" y1="50%" x2="50%" y2="84%" stroke="hsl(0 0% 0% / 0.07)" strokeWidth="1" />
          <line x1="50%" y1="50%" x2="16%" y2="50%" stroke="hsl(0 0% 0% / 0.07)" strokeWidth="1" />
        </svg>

        {/* live signal travelling the Trust Harness ring */}
        {!paused && (
          <div className="absolute inset-0 animate-vh-orbit" aria-hidden="true">
            <div
              className="absolute left-1/2 top-0 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary"
              style={{ boxShadow: "0 0 0 5px hsl(238 67% 52% / 0.12)" }}
            />
          </div>
        )}

        {/* system nodes + labels, orbiting inside the harness */}
        {SYSTEMS.map((s) => {
          const isActive = step.active === s.id;
          return (
            <div
              key={s.id}
              className="absolute flex flex-col items-center gap-1.5"
              style={{ ...polar(s.angleDeg, ORBIT), transform: "translate(-50%, -50%)" }}
            >
              <span
                className={`h-2.5 w-2.5 rounded-full transition-all duration-300 ${
                  isActive ? "bg-primary scale-150" : "bg-foreground/25"
                }`}
                style={isActive ? { boxShadow: "0 0 0 5px hsl(238 67% 52% / 0.14)" } : undefined}
              />
              <span
                className={`font-mono-tech text-[11px] uppercase tracking-[0.14em] whitespace-nowrap transition-colors duration-300 ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {s.text}
              </span>
            </div>
          );
        })}

        {/* center readout — Meshly */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex h-28 w-28 lg:h-32 lg:w-32 flex-col items-center justify-center rounded-full border border-foreground/10 bg-background text-center">
            <span className="text-sm font-semibold tracking-tight text-foreground">Meshly</span>
            <span className="mt-1 font-mono-tech text-[10px] font-medium uppercase tracking-[0.16em] text-primary">
              {step.verified ? "✓ " : ""}
              {step.word}
            </span>
          </div>
        </div>
      </div>

      {/* live caption */}
      <p className="h-5 font-mono-tech text-xs text-muted-foreground text-center transition-opacity duration-300">
        {step.caption}
      </p>
    </div>
  );
}
